import { CircleCheck, GitMerge, Hand, LoaderCircle, Zap } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Sparkline, StatStrip, StatTile, formatInt, formatMoney, formatMoneyCompact, formatPercent, plural, useNow } from '@orgus/ui';
import type { MachineLoadState } from '@orgus/protocol';
import { useCounts, useExt, useFeed, useMachines } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { getFleetCpuWaitingStats } from '../machine-load/cpuWaiting.ts';
import { useWaitingOnYou } from '../approvals/waiting.ts';
import { useCosts } from '../costs/useCosts.ts';
import { windowStart } from './flow.ts';
import { buckets, cumulative, heroCounts, landedTimes, submittedTimes } from './hero.ts';
import { useHeroInput } from './useHero.ts';

const HOUR = 60 * 60_000;

/** A tile's trend: a small line in the series colour, the last point marked, named for screen readers. */
const Trend = ({ values, label, slot }: { values: number[]; label: string; slot: 1 | 2 | 3 | 4 | 5 }) => <Sparkline values={values} width={64} height={24} min={0} slot={slot} label={label} />;

/**
 * The vitals (brief 4.1), as the hero shows them: Needs you, Working, In the queue, Landed today
 * and Spend today, each a big number and a small trend. Grey by default: only Needs you turns
 * amber, and only when something blocks. Each tile opens the page that lists what it counts; the
 * detail behind it (sessions, machines, CPU waiting, the month's spend) is its tooltip.
 */
export function CountStrip() {
  const navigate = useNavigate();
  const c = useCounts();
  const waiting = useWaitingOnYou();
  const feed = useFeed(500);
  const machines = useMachines();
  const costs = useCosts();
  const { input } = useHeroInput();
  const now = useNow(60_000);
  const n = useMemo(() => heroCounts(input), [input]);
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const cpu = useMemo(() => getFleetCpuWaitingStats(loadSlice), [loadSlice]);
  const load = useMemo(() => {
    const on = machines.filter((m) => m.online);
    return on.length ? on.reduce((a, m) => a + (m.load ?? 0), 0) / on.length : null;
  }, [machines]);

  const needsSpark = useMemo(() => buckets(feed.filter((e) => e.kind === 'session.waiting').map((e) => e.at), now, HOUR), [feed, now]);
  const workSpark = useMemo(() => buckets(feed.filter((e) => e.kind === 'session.started' || e.kind === 'subagent.started' || e.kind === 'workflow.started').map((e) => e.at), now, HOUR), [feed, now]);
  const queueSpark = useMemo(() => buckets(submittedTimes(input), now, 6 * HOUR), [input, now]);
  const hours = Math.max(2, Math.ceil((now - windowStart(now)) / HOUR));
  const landedSpark = useMemo(() => cumulative(buckets(landedTimes(input, now), now, hours * HOUR, hours)), [input, now, hours]);
  const spendSpark = useMemo(() => (costs?.byDay ?? []).slice(-14).map((d) => d.cost), [costs]);
  const dayBudget = costs?.budgetStatus.find((b) => b.periodEnd != null && b.periodEnd - b.periodStart <= 26 * HOUR);

  // The tile counts decisions, the way the Inbox and the rail badge count them. The bell in the
  // top bar counts unread notifications, a different thing: the tooltip says so.
  const answer = waiting.approvals.length + waiting.sessions.length + waiting.assistant;
  const asked = waiting.asked.length;
  // One short fact per tile (it never truncates at 1280 px); the full breakdown is the tooltip.
  const waitingSub = !waiting.total ? 'Nothing to decide' : waiting.blocked ? `${formatInt(waiting.blocked)} blocked` : answer ? `${formatInt(answer)} to answer` : `${formatInt(asked)} asked`;
  const waitingHint = [
    waiting.total ? [waiting.blocked && `${formatInt(waiting.blocked)} blocked`, waiting.approvals.length && plural(waiting.approvals.length, 'approval'), waiting.sessions.length && `${plural(waiting.sessions.length, 'session')} blocked`, asked && `${plural(asked, 'question')} asked`, waiting.assistant && plural(waiting.assistant, 'assistant step')].filter(Boolean).join(', ') : 'Nothing to decide',
    'Counted as the Inbox counts. The bell counts unread notifications.',
  ].join('. ');
  const workHint = [
    `${plural(n.live, 'live session')}: ${formatInt(n.working)} working, ${formatInt(n.idle)} idle`,
    `${formatInt(c.machinesOnline)} of ${plural(c.machinesTotal, 'machine')} online${load == null ? '' : `, ${formatPercent(load)} load`}`,
    c.agentsRunning ? `${plural(c.agentsRunning, 'subagent')} and workflows running` : '',
  ].filter(Boolean).join('. ');
  const queueWords = n.inQueue ? [n.verifying && `${formatInt(n.verifying)} verifying`, n.queued && `${formatInt(n.queued)} queued`].filter(Boolean).join(', ') : 'Nothing waiting';
  const queueSub = !n.inQueue ? 'Nothing waiting' : n.verifying ? `${formatInt(n.verifying)} verifying` : `${formatInt(n.queued)} queued`;
  const queueHint = `${queueWords}, across the landing queue and every pipeline${cpu.totalWaiting ? `. ${plural(cpu.totalWaiting, 'run')} waiting for CPU` : ''}`;

  return (
    <StatStrip label="Live counts">
      <StatTile
        label="Needs you"
        help="Decisions only you can make: approvals, blocked sessions and questions."
        icon={<Hand />}
        value={waiting.total}
        attention={waiting.urgent > 0}
        sub={waitingSub}
        hint={waitingHint}
        trend={<Trend values={needsSpark} slot={2} label={`Waits in the last hour: ${formatInt(needsSpark.reduce((a, x) => a + x, 0))}`} />}
        onOpen={() => navigate('/inbox')}
      />
      <StatTile
        label="Working"
        help="Agents working on a task right now."
        icon={<LoaderCircle />}
        value={n.working}
        unit={n.working === 1 ? 'agent' : 'agents'}
        sub={n.live ? `of ${formatInt(n.live)} live` : 'No live sessions'}
        hint={workHint}
        trend={<Trend values={workSpark} slot={1} label={`Starts in the last hour: ${formatInt(workSpark.reduce((a, x) => a + x, 0))}`} />}
        onOpen={() => navigate('/sessions')}
      />
      <StatTile
        label="In the queue"
        help="Changes waiting to land: queued, or being verified now."
        icon={<GitMerge />}
        value={n.inQueue}
        sub={queueSub}
        hint={queueHint}
        trend={<Trend values={queueSpark} slot={5} label={`Submitted in the last 6 hours: ${formatInt(queueSpark.reduce((a, x) => a + x, 0))}`} />}
        onOpen={() => navigate('/pipelines/landing')}
      />
      <StatTile
        label="Landed today"
        help="Changes that reached a main branch since midnight."
        icon={<CircleCheck />}
        value={n.landedToday}
        sub={n.failedToday ? `${formatInt(n.failedToday)} failed` : 'Nothing failed'}
        trend={<Trend values={landedSpark} slot={3} label={`Landed so far today: ${formatInt(n.landedToday)}`} />}
        onOpen={() => navigate('/pipelines')}
      />
      <StatTile
        label="Spend today"
        help="What your agents cost today, across every account."
        icon={<Zap />}
        value={costs?.today ?? 0}
        format={formatMoneyCompact}
        sub={costs ? (dayBudget ? `of ${formatMoneyCompact(dayBudget.limit)} a day` : `${formatMoneyCompact(costs.month)} this month`) : 'None reported'}
        hint={costs ? `${formatMoney(costs.today)} today${dayBudget ? ` of ${formatMoney(dayBudget.limit)} daily budget` : ''}. ${formatMoney(costs.month)} this month, ${formatMoney(costs.forecastMonth)} forecast` : 'This hub reports no spend yet.'}
        trend={<Trend values={spendSpark} slot={4} label={`Spend, last ${formatInt(spendSpark.length)} days`} />}
        onOpen={() => navigate('/spend')}
      />
    </StatStrip>
  );
}
